import { Footer } from '../components/footer';
import { Camp67Header } from '../components/camp67-header/camp67-header';
import { MetaFunction } from '@remix-run/cloudflare';
import { Link } from '@remix-run/react';
import { useMessages } from '../i18n/useMessages';

export const meta: MetaFunction = () => {
  return [
    { title: 'Contact Us - camp67' },
    {
      name: 'description',
      content: "Get in touch with us. We're here to help!",
    },
    {
      tagName: 'link',
      name: 'canonical',
      href: 'https://camp67.com/contact-us',
    },
  ];
};

export default function ContactUsPage() {
  const getMessage = useMessages();
  return (
    <div className="w-full bg-gradient-to-b from-orange-50 via-white to-yellow-50 min-h-[100vh]">
      <div className="m-auto bg-white w-full rounded-b-md border-dotted border-b-orange-200 border-b pb-4">
        <Camp67Header
          isLogoLight={false}
          isDarkGlowHidden={true}
          textColor="dark"
          isClearBackground
        />
      </div>

      <main className="pt-12 pb-20 md:pt-16 md:pb-28">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-4">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl font-bold font-serif text-gray-900 mb-4">
              {getMessage('camp67-contact.contact-us')}
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              We&apos;d love to hear from you. Reach out for bookings, inquiries, or just
              to say hello.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Camp Location */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">📍 Camp Location</h3>
              <div className="space-y-2 text-gray-600">
                <p className="font-semibold text-orange-600 text-sm">
                  Open: 31 Dec 2025 – End of Feb 2026
                </p>
                <p className="font-semibold text-gray-800">Kumbh Sector No. 22</p>
                <p className="text-sm leading-relaxed">
                  Maharshi Sadafal Deo Ashram,
                  <br />
                  Sadafal Road, Dewrakh Kachhar,
                  <br />
                  Prayagraj, UP 211019
                </p>
                <Link
                  className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium text-sm mt-2 transition-colors"
                  to="https://maps.app.goo.gl/yMaLiC9mCLvdcMrJ9"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Open in Google Maps →
                </Link>
              </div>
            </div>

            {/* Bookings & Inquiries */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Bookings & Inquiries
              </h3>
              <div className="space-y-2 text-gray-600">
                <p className="flex items-center gap-2">
                  <span className="text-green-600">📱</span>
                  <a href="https://wa.me/918485835675" className="hover:text-orange-600">
                    WhatsApp/Phone: +91 84858 35675
                  </a>
                </p>
              </div>
            </div>

            {/* On-Ground Support */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">On-Ground Support</h3>
              <div className="space-y-2 text-gray-600">
                <p className="text-sm text-gray-500 mb-2">Mela Visits / Boat / Sangam</p>
                <p className="flex items-center gap-2">
                  <span className="text-amber-600">📞</span>
                  <a href="tel:+919910078872" className="hover:text-orange-600">
                    +91 99100 78872
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-amber-600">📞</span>
                  <a href="tel:+918065249725" className="hover:text-orange-600">
                    080652 49725
                  </a>
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Email Us</h3>
              <div className="space-y-2 text-gray-600">
                <p className="flex items-center gap-2">
                  <span className="text-blue-600">✉️</span>
                  <a href="mailto:bookings@camp67.com" className="hover:text-orange-600">
                    bookings@camp67.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Office Address */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Office Address</h3>
                <p className="text-gray-600 leading-relaxed">
                  2nd Floor, 61/62, Pocket C-5,
                  <br />
                  Rohini Sector-11, Delhi,
                  <br />
                  North West, Delhi, India
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link
              prefetch="intent"
              to="/terms-of-service"
              className="text-gray-600 hover:text-orange-600 underline text-sm transition-colors"
            >
              {getMessage('camp67-contact.see-tos')} →
            </Link>
            <Link
              prefetch="intent"
              to="/privacy-policy"
              className="text-gray-600 hover:text-orange-600 underline text-sm transition-colors"
            >
              Privacy Policy →
            </Link>
            <Link
              prefetch="intent"
              to="/cancellation-and-refund-policy"
              className="text-gray-600 hover:text-orange-600 underline text-sm transition-colors"
            >
              Refund Policy →
            </Link>
          </div>

          {/* Company Info */}
          <div className="text-center">
            <p className="text-sm text-gray-500">
              {getMessage('camp67-contact.offered-by')}{' '}
              <a
                href="https://sukritwellness.com"
                className="text-orange-600 hover:underline font-medium"
              >
                Sukrit Wellness Tours Private Limited
              </a>
            </p>
          </div>
        </div>
      </main>
      <Footer isHome={false} />
    </div>
  );
}
